import React, {useState} from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {PageTitle, RecordPage} from "../../shared/components/record/RecordKit";
import "./SettingsPage.css";

/*
 * The Settings page, in Android's design (estimatebuilder/settings/
 * SettingsScreen.kt): the page title with Log out beside it (sign-out lives
 * here, not on the estimates board), confirmed with Android's "Log out?"
 * dialog. The whole page is the record pages' 750px column (RecordKit,
 * SQ-574).
 *
 * SQ-743: nothing that used to be here belongs here. Team & access is the
 * Team Members menu item's page, Job pricing has its own menu item, the
 * plan is on the Account page, and the connected accounts (business email,
 * Google Calendar) and the photo quality are under the Account page's
 * Business details. What stays is help and the legal pages; settings for
 * other parts of the app will join them here.
 */

/** A row that opens a page: its name, what it is, and a chevron. */
const LinkRow = ({title, blurb, onClick}: {title: string; blurb: string; onClick: () => void}) => (
  <button type="button" className="sp-link-row" onClick={onClick}>
    <span className="sp-account-text">
      <span className="sp-account-name">{title}</span>
      <span className="sp-account-blurb">{blurb}</span>
    </span>
    <svg className="sp-link-chevron" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 5.5L15.5 12L9 18.5" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </button>
);

export const SettingsPage = () => {
  const navigate = useNavigate();
  const [confirmLogout, setConfirmLogout] = useState(false);

  return (
    <RecordPage name="SettingsPage" className="sp">
      <PageTitle title="Settings">
        <button type="button" className="sp-logout-btn" onClick={() => setConfirmLogout(true)}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15 17L20 12L15 7" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M20 12H9" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 19H6C5.44772 19 5 18.5523 5 18V6C5 5.44772 5.44772 5 6 5H12" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Log out
        </button>
      </PageTitle>

      <div className="sp-group">
        <h2 className="sp-group-title">Help &amp; legal</h2>
        <div className="sp-card">
          <LinkRow title="Customer support" blurb="Questions, problems or feedback: how to reach us" onClick={() => navigate('/support')}/>
          <div className="sp-hairline"/>
          <LinkRow title="Privacy policy" blurb="What we collect and how we use it" onClick={() => navigate('/privacy-policy')}/>
          <div className="sp-hairline"/>
          <LinkRow title="User agreements" blurb="The terms of using Savaquan" onClick={() => navigate('/terms-of-service')}/>
        </div>
      </div>

      {/* The person's own account (the apps' Account page): the company's
          connected accounts, photo quality and plan are there; the menu's
          foot opens it too. */}
      <button type="button" className="sp-account-link" onClick={() => navigate('/account')}>
        Your account
      </button>

      <Dialog open={confirmLogout} onClose={() => setConfirmLogout(false)} aria-labelledby="sp-logout-title">
        <DialogTitle id="sp-logout-title">Log out?</DialogTitle>
        <DialogContent>
          <DialogContentText>You'll need your phone number or email to get back in.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLogout(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => navigate('/logout')}>Log out</Button>
        </DialogActions>
      </Dialog>
    </RecordPage>
  );
}
